import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { CreateProjectForm } from "./_components/create-project-form";
import { useParams, useNavigate } from "react-router-dom";
import type { projectIdKey } from "@/routes/company/routes.projects.constants";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";
import { Loader as LoaderIcon } from "lucide-react";
import { PATHS } from "@/routes/paths";

export const CreateProjectPage = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { projectId } = useParams<{ [projectIdKey]: string }>();
  const isUpdating = Boolean(projectId);
  usePageTitle(
    isUpdating ? t("projects:edit_project") : t("projects:create_project"),
  );
  const navigate = useNavigate();
  const { can, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (isUpdating && !can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_edit_projects")}
        onBack={() => navigate(PATHS.COMPANY.COMPANY_PROJECTS_PATHS.ROOT)}
      />
    );
  }

  if (!isUpdating && !can(PLATFORM_SECTION_ENUM.PROJECTS, "create")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_create_projects")}
        onBack={() => navigate(PATHS.COMPANY.COMPANY_PROJECTS_PATHS.ROOT)}
      />
    );
  }

  return <CreateProjectForm projectId={projectId} isUpdating={isUpdating} />;
};
